import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export const OUTCOME_LABEL = {
  shipped: 'Shipped',
  circuit_breaker: 'Hit the circuit breaker',
  abandoned: 'Abandoned',
};

const OUTCOME_HELP = {
  shipped: 'Done and in production. The bet paid off.',
  circuit_breaker: 'Not finished when time ran out. It stops here and has to win a new bet to come back.',
  abandoned: 'Called off. We are not coming back to this.',
};

/**
 * The closing conversation for a cycle: name an outcome for every bet.
 *
 * The circuit breaker is what makes a Shape Up cycle mean anything. Unfinished
 * work does not get extended by default; it stops, and has to win a new bet
 * against everything else being shaped. Making that an explicit choice per bet is
 * the whole point of this dialog, so there is no "carry over" option to reach for.
 */
const CloseCycleDialog = ({ open, cycle, bets = [], onClose, onSubmit }) => {
  const [choices, setChoices] = useState({});
  const [archiveChannels, setArchiveChannels] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Each opening starts from what the scopes suggest, so the common case is
  // confirming rather than filling in a form.
  useEffect(() => {
    if (!open) return;
    setChoices(bets.reduce((acc, bet) => {
      acc[bet.id] = {
        outcome: bet.outcome || bet.proposedOutcome || 'circuit_breaker',
        note: bet.outcomeNote || '',
      };
      return acc;
    }, {}));
    // Off by default. A closed cycle usually means the conversation is over, but
    // not always, and archiving somebody's channel is not a thing to do on a guess.
    setArchiveChannels(false);
    setError(null);
  }, [open, bets]);

  const update = (betId, patch) => {
    setChoices(current => ({ ...current, [betId]: { ...current[betId], ...patch } }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(
        bets.map(bet => ({
          betId: bet.id,
          outcome: choices[bet.id]?.outcome,
          note: choices[bet.id]?.note,
        })),
        { archiveChannels: archiveChannels && channelCount > 0 }
      );
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not close the cycle.');
    } finally {
      setSaving(false);
    }
  };

  const breaking = bets.filter(bet => choices[bet.id]?.outcome === 'circuit_breaker').length;
  const channelCount = bets.filter(bet => bet.slackChannelId).length;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Close {cycle?.name || 'this cycle'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Name how each bet ended. Nothing carries into the next cycle on its own:
          work that did not finish has to be shaped again and win a new bet.
        </DialogContentText>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {bets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            This cycle has no bets, so there is nothing to record.
          </Typography>
        ) : (
          <Stack spacing={2} divider={<Divider flexItem />}>
            {bets.map((bet) => {
              const choice = choices[bet.id] || {};
              return (
                <Box key={bet.id}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{bet.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {bet.stats.done} of {bet.stats.total} scopes done
                      {bet.stats.hammered > 0 && `, ${bet.stats.hammered} hammered`}
                    </Typography>
                    {(bet.attempt || 1) > 1 && (
                      <Chip size="small" color="warning" variant="outlined" label={`Attempt ${bet.attempt}`} />
                    )}
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      select
                      size="small"
                      label="Outcome"
                      value={choice.outcome || 'circuit_breaker'}
                      onChange={(e) => update(bet.id, { outcome: e.target.value })}
                      sx={{ minWidth: 210 }}
                      helperText={OUTCOME_HELP[choice.outcome]}
                    >
                      {Object.entries(OUTCOME_LABEL).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      size="small"
                      label="What happened"
                      placeholder="Optional, but the part you will want next cycle"
                      value={choice.note || ''}
                      onChange={(e) => update(bet.id, { note: e.target.value })}
                      fullWidth
                      multiline
                      maxRows={3}
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}

        {breaking > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {breaking === 1 ? 'One bet stops here' : `${breaking} bets stop here`}. You can bet on
            {breaking === 1 ? ' it' : ' them'} again from the cycle page, which records it as another attempt.
          </Alert>
        )}

        {channelCount > 0 && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={archiveChannels}
                  onChange={(e) => setArchiveChannels(e.target.checked)}
                  disabled={saving}
                />
              )}
              label={`Archive ${channelCount === 1 ? 'the Slack channel' : `all ${channelCount} Slack channels`} for this cycle`}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
              Archiving hides a channel without deleting it. The links on each bet keep
              working, so the history stays reachable.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Closing…' : 'Close the cycle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseCycleDialog;
